import React, { useState, useMemo, useCallback } from 'react';
import { ProductEntity } from 'sl-api-connector/types';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { safeDivide } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useAppSelector } from 'src/utils/Hooks';
import { useBrandEntity, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';

import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { useAtlasV2TableData } from 'src/serverProxy/useAtlasV2TableData';
import { HIDE_GROUP_BY_LEVEL } from 'src/components/Layout/AtlasV2PageLayout';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';

export const selectedIdLink = {
  brandId: 'brand',
  categoryId: 'category',
  subCategoryId: 'subcategory'
};

const AtlasV2ConversionTopEntityChart = () => {
  const { isBrandEntity } = useBrandEntity();
  const [selectedId, setSelectedId] = useState(isBrandEntity ? 'categoryId' : 'brandId');
  const mainEntityType = useMainEntityType();
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  const { startWeek: comparisonStartWeekId, endWeek: comparisonEndWeekId } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const { data: unitsSoldData, isLoading: unitsSoldLoading } = useAtlasV2TableData({
    indexName: 'sales',
    fields: [{ name: 'unitsSold' }],
    groupByFieldName: selectedId,
    itemsPerPage: 50,
    pageNumber: 1,
    requestId: 'traffic-conversion-rate-topEntity-unitsSold',
    sortFieldName: 'unitsSold'
  });

  const fieldIds = useMemo(
    () => unitsSoldData.map((row: { metadata: { product: ProductEntity } }) => row.metadata[selectedId]),
    [unitsSoldData, selectedId]
  );

  const { data: trafficData, isLoading: trafficLoading } = useAtlasV2TableData({
    indexName: 'traffic',
    fields: [{ name: 'totalClicks' }],
    groupByFieldName: selectedId,
    itemsPerPage: 50,
    pageNumber: 1,
    requestId: 'traffic-conversion-topEntity-traffic',
    useGenericAdvancedSearchArgs: { shouldPerformFetch: !unitsSoldLoading },
    buildRequest: (builder) => builder.addConditionTermFilter(selectedId, 'should', fieldIds)
  });

  const trafficDataMap = useMemo(() => {
    const map = {};
    trafficData.forEach((entity) => {
      map[`${entity.metadata.name}-${entity.metadata[selectedId]}`] = {
        currentTotalTraffic: entity.totalClicks_sum_value,
        compareTotalTraffic: entity[`totalClicks_sum_value_${comparisonStartWeekId}_${comparisonEndWeekId}`]
      };
    });
    return map;
  }, [trafficData, selectedId, comparisonStartWeekId, comparisonEndWeekId]);

  const { currentConversionData, compareConversionData, conversionMetaData } = useMemo(() => {
    const currentData = [];
    const compareData = [];
    const metaData = [];

    unitsSoldData.forEach((unitsSoldDatum) => {
      const propertySelector = `${unitsSoldDatum.metadata.name}-${unitsSoldDatum.metadata[selectedId]}`;

      if (trafficDataMap[propertySelector]) {
        const currentRate = safeDivide(
          unitsSoldDatum.unitsSold_sum_value,
          trafficDataMap[propertySelector].currentTotalTraffic
        );

        const compareRate = safeDivide(
          unitsSoldDatum[`unitsSold_sum_value_${comparisonStartWeekId}_${comparisonEndWeekId}`],
          trafficDataMap[propertySelector].compareTotalTraffic
        );

        currentData.push([unitsSoldDatum.metadata.name, currentRate]);
        compareData.push([unitsSoldDatum.metadata.name, compareRate]);

        metaData.push({
          name: unitsSoldDatum.metadata?.name,
          id: unitsSoldDatum.metadata[selectedId],
          type: selectedIdLink[selectedId] || ''
        });
      }
    });

    return { currentConversionData: currentData, compareConversionData: compareData, conversionMetaData: metaData };
  }, [unitsSoldData, selectedId, trafficDataMap, comparisonStartWeekId, comparisonEndWeekId]);

  const handleDropdownChange = useCallback((option) => setSelectedId(option.id), []);

  const dropdownOptions = useMemo(
    () =>
      [
        { id: 'brandId', label: 'Brand' },
        { id: 'categoryId', label: 'Category' },
        { id: 'subCategoryId', label: 'Subcategory' },
        { id: 'parentBrandId', label: 'Manufacturer' }
      ].filter(
        (option) =>
          !option.id.toLowerCase().startsWith(mainEntityType) &&
          HIDE_GROUP_BY_LEVEL?.[mainEntityType]?.[option.id] !== true
      ),
    [mainEntityType]
  );

  const noSeriesData = !currentConversionData?.length && !compareConversionData?.length;

  return (
    <BasePageContainer>
      <BeaconChartWithLegend
        title="Conversion Rate by Brand"
        singleDropdownProps={{
          prefix: 'Conversion Rate by',
          selectedId,
          onChange: handleDropdownChange,
          options: dropdownOptions
        }}
        primaryLegendProps={{
          displayName: legendLabel
        }}
        comparisonLegendProps={{
          displayName: legendComparisonLabel
        }}
        loading={trafficLoading || unitsSoldLoading}
        convertSeriesToDelimitedData={async () => {
          const label = `Conversion Rate`;
          return saveBarChartAsDelimitedData(currentConversionData, compareConversionData, {
            fileTitle: `${label}`,
            metricLabel: label
          });
        }}
      >
        {noSeriesData ? (
          <NoDataPlaceHolder
            title="Data currently unavailable"
            description="There is currently no data matching your selected criteria. Please try another query to view insights."
            flexProps={{ margin: '80px auto' }}
          />
        ) : (
          <TopEntityChartV2
            chartSeries={[
              {
                type: 'column',
                data: compareConversionData,
                metricType: METRICTYPE.PERCENT,
                categories: conversionMetaData
              },
              {
                type: 'column',
                data: currentConversionData,
                metricType: METRICTYPE.PERCENT
              }
            ]}
          />
        )}
      </BeaconChartWithLegend>
    </BasePageContainer>
  );
};

export default AtlasV2ConversionTopEntityChart;
